import { useState, useMemo, Fragment } from 'react'
import { Card, Table, Tag, Title, Text, Icon } from '@nimbus-ds/components'
import {
  ArrowsVerticalIcon,
  ArrowupIcon,
  ArrowDownIcon,
  ArrowsHorizontalIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@nimbus-ds/icons'

interface ChannelCell {
  sessions: number
  conversions: number
  sessions_with_origin: number
}

interface Coverage {
  sessions_total: number
  sessions_with_origin: number
}

interface TrafficSourceTableProps {
  // GA4-style channel rollup (one-per-session), window-aggregated. Keyed by GA4 channel
  // name + a reserved `unattributed` (orphaned conversions). Conversions are REAL, not
  // estimated — but per-channel CR is a LOWER BOUND (orphan bias relocates into rates).
  channels: Record<string, ChannelCell>
  // GA4 Default Channel Group session totals — reconciliation reference. null if no GA4.
  ga4ChannelTotals?: Record<string, { sessions: number }> | null
  // Store-level origin-capture coverage (window aggregate) for the panel chip.
  coverage?: Coverage | null
  // Per-channel campaign drill-down (P2), keyed by channel. Expands a channel row into
  // its campaigns (+ untagged/other). Absent/empty where no campaign data exists yet.
  breakdown?: Record<string, ChannelBreakdown> | null
}

interface ChannelBreakdown {
  sources: Record<string, { sessions: number; conversions: number }>
  campaigns: Array<{ campaign: string; medium: string | null; sessions: number; conversions: number }>
  untagged: { sessions: number; conversions: number }
  other: { sessions: number; conversions: number }
}

interface ChannelRow {
  name: string
  channelKey: string
  sessions: number
  conversions: number
  conversionRate: number
  vsAverage: number
  coverage: number          // per-channel origin coverage (0..1)
  ga4Sessions: number | null
}

type SortKey = 'sessions' | 'conversions' | 'conversionRate' | 'vsAverage'
type SortDirection = 'asc' | 'desc'

const UNATTRIBUTED = 'unattributed'

// Spanish labels for GA4 channel names — kept in sync with TrafficSourceTimeline's CHANNELS.
const CHANNEL_LABELS: Record<string, string> = {
  'Paid Search': 'Búsqueda pagada',
  'Paid Social': 'Social pagado',
  'Organic Search': 'Búsqueda orgánica',
  'Organic Social': 'Social orgánico',
  'Email': 'Email',
  'Referral': 'Referidos',
  'Paid Other': 'Otros pagados',
  'Direct': 'Directo',
}

export function TrafficSourceTable({ channels, ga4ChannelTotals, coverage, breakdown }: TrafficSourceTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('sessions')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExpand = (key: string) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const { rows: channelData, unattributedConversions, totalConversions } = useMemo(() => {
    const entries = Object.entries(channels || {})
    const unattributed = channels?.[UNATTRIBUTED]?.conversions || 0
    const totalConv = entries.reduce((s, [, c]) => s + (c?.conversions || 0), 0)

    const real = entries
      .filter(([key]) => key !== UNATTRIBUTED)
      .map(([key, cell]) => ({
        name: CHANNEL_LABELS[key] || key,
        channelKey: key,
        sessions: cell?.sessions || 0,
        conversions: cell?.conversions || 0,
        coverage: cell?.sessions ? (cell.sessions_with_origin || 0) / cell.sessions : 0,
        ga4Sessions: ga4ChannelTotals?.[key]?.sessions ?? null,
      }))
      .filter(r => r.sessions >= 5) // hide noise

    // Sessions-weighted average CR across shown channels (the "vs promedio" base)
    const totalSessions = real.reduce((s, r) => s + r.sessions, 0)
    const totalRealConv = real.reduce((s, r) => s + r.conversions, 0)
    const avgRate = totalSessions > 0 ? (totalRealConv / totalSessions) * 100 : 0

    const rows: ChannelRow[] = real.map(r => {
      const rate = r.sessions > 0 ? (r.conversions / r.sessions) * 100 : 0
      return {
        ...r,
        conversionRate: rate,
        vsAverage: avgRate > 0 ? rate / avgRate : 1,
      }
    })
    return { rows, unattributedConversions: unattributed, totalConversions: totalConv }
  }, [channels, ga4ChannelTotals])

  const sourceData = channelData
  const hasGA4 = !!ga4ChannelTotals && Object.keys(ga4ChannelTotals).length > 0
  const coveragePct = coverage && coverage.sessions_total > 0
    ? Math.round((coverage.sessions_with_origin / coverage.sessions_total) * 100)
    : null
  const unattributedPct = totalConversions > 0
    ? Math.round((unattributedConversions / totalConversions) * 100)
    : 0

  const sortedData = useMemo(() => {
    return [...sourceData].sort((a, b) => {
      const aVal = a[sortKey]
      const bVal = b[sortKey]
      const multiplier = sortDirection === 'desc' ? -1 : 1
      return (aVal - bVal) * multiplier
    })
  }, [sourceData, sortKey, sortDirection])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')
    } else {
      setSortKey(key)
      setSortDirection('desc')
    }
  }

  const getVsAverageDisplay = (vsAvg: number): { icon: JSX.Element; text: string; appearance: 'success' | 'danger' | 'neutral' } => {
    if (vsAvg >= 1.2) {
      return {
        icon: <Icon source={<ArrowupIcon />} color="currentColor" />,
        text: `${vsAvg.toFixed(1)}x`,
        appearance: 'success',
      }
    } else if (vsAvg <= 0.8) {
      return {
        icon: <Icon source={<ArrowDownIcon />} color="currentColor" />,
        text: `${vsAvg.toFixed(1)}x`,
        appearance: 'danger',
      }
    }
    return {
      icon: <Icon source={<ArrowsHorizontalIcon />} color="currentColor" />,
      text: `${vsAvg.toFixed(1)}x`,
      appearance: 'neutral',
    }
  }

  const SortableHeader = ({ label, sortKeyValue }: { label: string; sortKeyValue: SortKey }) => (
    <button
      onClick={() => toggleSort(sortKeyValue)}
      className="flex items-center gap-1 hover:text-foreground transition-colors"
    >
      {label}
      <span className={sortKey === sortKeyValue ? 'text-primary' : 'text-muted-foreground/50'}>
        <Icon source={<ArrowsVerticalIcon />} color="currentColor" />
      </span>
    </button>
  )

  const header = (
    <Card.Header>
      <div className="flex items-start justify-between gap-2">
        <div>
          <Title as="h3">Rendimiento por Canal de Tráfico</Title>
          <Text>Cómo agrupa GA4 el origen de tus visitantes</Text>
        </div>
        {coveragePct !== null && (
          <div className="shrink-0 whitespace-nowrap" title="Sesiones con origen capturado por TNX (el resto usa el referente). Mayor cobertura = atribución más confiable.">
            <Tag appearance="neutral">
              Origen en {coveragePct}% de sesiones
            </Tag>
          </div>
        )}
      </div>
    </Card.Header>
  )

  if (sourceData.length === 0) {
    return (
      <Card>
        {header}
        <Card.Body>
          <div className="flex items-center justify-center py-8">
            <Text>No hay suficientes datos de canales de tráfico disponibles.</Text>
          </div>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card>
      {header}
      <Card.Body>
        <Table minWidth="720px" stickyScrollbar>
          <Table.Head>
            <Table.Row>
              <Table.Cell as="th" className="w-[160px]">Canal</Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Sesiones" sortKeyValue="sessions" />
              </Table.Cell>
              {hasGA4 && <Table.Cell as="th" className="text-right text-muted-foreground">GA4</Table.Cell>}
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Conv." sortKeyValue="conversions" />
              </Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Tasa (≥)" sortKeyValue="conversionRate" />
              </Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="vs Promedio" sortKeyValue="vsAverage" />
              </Table.Cell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {sortedData.map((row) => {
              const vsDisplay = getVsAverageDisplay(row.vsAverage)
              const ga4Title = row.ga4Sessions != null
                ? `GA4: ${row.ga4Sessions.toLocaleString()} · TNX captó ${row.ga4Sessions > 0 ? Math.round((row.sessions / row.ga4Sessions) * 100) : 0}% · cobertura origen ${Math.round(row.coverage * 100)}%`
                : 'Sin referencia GA4 para este canal'
              const bd = breakdown?.[row.channelKey]
              const drillRows = bd
                ? [
                    ...bd.campaigns.map(c => ({ label: c.campaign, medium: c.medium, sessions: c.sessions, conversions: c.conversions, muted: false })),
                    ...(bd.untagged.sessions > 0 ? [{ label: 'Sin campaña', medium: null, sessions: bd.untagged.sessions, conversions: bd.untagged.conversions, muted: true }] : []),
                    ...(bd.other.sessions > 0 ? [{ label: 'Otras campañas', medium: null, sessions: bd.other.sessions, conversions: bd.other.conversions, muted: true }] : []),
                  ]
                : []
              // Residual = channel total (full window) − what the breakdown covers
              // (campaign data only exists for recent days). Surfaced so the rows reconcile
              // to the channel total instead of silently under-summing.
              if (bd) {
                const bdS = drillRows.reduce((s, d) => s + d.sessions, 0)
                const bdC = drillRows.reduce((s, d) => s + d.conversions, 0)
                const resS = row.sessions - bdS
                const resC = row.conversions - bdC
                if (resS > 0) drillRows.push({ label: 'Sin datos de campaña', medium: null, sessions: resS, conversions: Math.max(0, resC), muted: true })
              }
              const canExpand = drillRows.length > 0
              const isOpen = expanded.has(row.channelKey)
              return (
                <Fragment key={row.channelKey}>
                  <Table.Row className={canExpand ? 'cursor-pointer' : ''} onClick={canExpand ? () => toggleExpand(row.channelKey) : undefined}>
                    <Table.Cell className="font-medium">
                      <span className="flex items-center gap-1">
                        {canExpand
                          ? (isOpen ? <Icon source={<ChevronDownIcon />} /> : <Icon source={<ChevronRightIcon />} />)
                          : <span className="w-3.5" />}
                        {row.name}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="text-right font-mono">{row.sessions.toLocaleString()}</Table.Cell>
                    {hasGA4 && (
                      <Table.Cell className="text-right font-mono text-muted-foreground text-xs" title={ga4Title}>
                        {row.ga4Sessions != null ? row.ga4Sessions.toLocaleString() : '—'}
                      </Table.Cell>
                    )}
                    <Table.Cell className="text-right font-mono">{row.conversions.toLocaleString()}</Table.Cell>
                    <Table.Cell className="text-right font-mono">≥ {row.conversionRate.toFixed(2)}%</Table.Cell>
                    <Table.Cell className="text-right">
                      <Tag appearance={vsDisplay.appearance}>
                        {vsDisplay.icon}
                        {vsDisplay.text}
                      </Tag>
                    </Table.Cell>
                  </Table.Row>
                  {isOpen && drillRows.map((d, i) => {
                    const rate = d.sessions > 0 ? (d.conversions / d.sessions) * 100 : 0
                    return (
                      <Table.Row key={`${row.channelKey}:${d.label}:${i}`} className="bg-muted/30 text-sm">
                        <Table.Cell className={`pl-8 ${d.muted ? 'italic text-muted-foreground' : ''}`}>
                          {d.label}
                          {d.medium ? <span className="ml-1 text-xs text-muted-foreground">· {d.medium}</span> : null}
                        </Table.Cell>
                        <Table.Cell className="text-right font-mono">{d.sessions.toLocaleString()}</Table.Cell>
                        {hasGA4 && <Table.Cell className="text-right font-mono">—</Table.Cell>}
                        <Table.Cell className="text-right font-mono">{d.conversions.toLocaleString()}</Table.Cell>
                        <Table.Cell className="text-right font-mono">≥ {rate.toFixed(2)}%</Table.Cell>
                        <Table.Cell className="text-right font-mono">—</Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Fragment>
              )
            })}
            {unattributedConversions > 0 && (
              <Table.Row className="text-muted-foreground">
                <Table.Cell className="font-medium italic">Sin atribuir</Table.Cell>
                <Table.Cell className="text-right font-mono">—</Table.Cell>
                {hasGA4 && <Table.Cell className="text-right font-mono">—</Table.Cell>}
                <Table.Cell className="text-right font-mono">{unattributedConversions.toLocaleString()}</Table.Cell>
                <Table.Cell className="text-right font-mono">—</Table.Cell>
                <Table.Cell className="text-right font-mono">—</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
        <div className="mt-4">
          <Text fontSize="caption">
            Tasa de conversión por canal es un <strong>piso</strong> (≥): {unattributedPct}% de las conversiones no
            pudieron atribuirse a ningún canal y se muestran como “Sin atribuir”. No compares tasas entre canales como
            valores exactos.
          </Text>
        </div>
      </Card.Body>
    </Card>
  )
}
